import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CalendarDays, Tag } from "lucide-react";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { usePageMeta, SITE_PUBLIC_URL } from "../components/site/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); } catch { return ""; } };

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [tag, setTag] = useState("");
  const [loading, setLoading] = useState(true);

  usePageMeta({
    title: "Blog — Home Repair Tips & Contractor Advice | Home Pro Connect",
    description: "Practical guides for South Carolina homeowners: choosing contractors, planning remodels, and avoiding costly mistakes — from Home Pro Connect.",
    path: "/blog/",
  });

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/blog/posts`, { params: tag ? { tag } : {} })
      .then(({ data }) => setPosts(data.posts || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [tag]);

  const tags = [...new Set(posts.flatMap((p) => p.tags || []))];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <section className="bg-[#133955] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-16 md:pt-32 md:pb-20">
          <p className="text-[#54BCE6] font-bold text-sm uppercase tracking-[0.2em] mb-3">The Home Pro Connect Blog</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold max-w-2xl">Home repair know-how for South Carolina homeowners</h1>
          <p className="mt-4 text-white/75 text-base md:text-lg max-w-xl">Guides on hiring contractors, planning projects, and getting repairs done right the first time.</p>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="blog-list">
        <section data-testid="blog-intro" className="max-w-3xl mb-12">
          <div className="space-y-4 text-base leading-relaxed text-[#173042]/80">
            <p>
              Owning a home in the South Carolina Lowcountry comes with its own set of challenges —
              humidity that never quits, storm season every summer, salt air near the coast, and
              older housing stock that always seems to need one more repair. This blog exists to
              help local homeowners make sense of it all.
            </p>
            <p>
              Here we publish practical guides on hiring and working with contractors: what to ask
              before you sign anything, how to compare estimates, which projects need a general
              contractor and which ones a specialist can handle, and how to plan remodels,
              additions, and repairs without costly surprises. We also cover seasonal home
              maintenance specific to our region, from roof care ahead of hurricane season to HVAC
              upkeep through long, hot summers.
            </p>
            <p>
              Every article is written for homeowners in Summerville, Charleston, North Charleston,
              Goose Creek, Mount Pleasant, Myrtle Beach, and the surrounding communities. When a
              guide leaves you ready to start a project, you can request a connection and we will
              help match you with a contractor from our network.
            </p>
          </div>
        </section>
        {tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="blog-tag-filter">
            <Tag size={14} className="text-[#173042]/50" />
            <button
              data-testid="blog-tag-all"
              onClick={() => setTag("")}
              className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${!tag ? "bg-[#133955] text-white" : "bg-[#EAF7FC] text-[#133955] hover:bg-[#54BCE6]/30"}`}
            >All</button>
            {tags.map((t) => (
              <button
                key={t}
                data-testid={`blog-tag-${t.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setTag(t)}
                className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${tag === t ? "bg-[#133955] text-white" : "bg-[#EAF7FC] text-[#133955] hover:bg-[#54BCE6]/30"}`}
              >{t}</button>
            ))}
          </div>
        )}

        {loading ? (
          <p className="text-[#173042]/60" data-testid="blog-loading">Loading posts…</p>
        ) : posts.length === 0 ? (
          <div className="rounded-2xl bg-[#EAF7FC] p-10 text-center" data-testid="blog-empty">
            <p className="font-bold text-[#133955] text-lg">No posts yet</p>
            <p className="text-[#173042]/60 text-sm mt-1">Check back soon — we're working on our first guides.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group rounded-2xl border border-[rgba(19,57,85,0.08)] bg-white overflow-hidden hover:border-[#54BCE6] hover:shadow-[0_16px_40px_rgba(19,57,85,0.10)] transition-all duration-300 flex flex-col"
              >
                {p.cover_file ? (
                  <img
                    src={`${API}/files/${p.cover_file.file_id}`}
                    alt={p.title}
                    className="w-full h-44 object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-44 bg-gradient-to-br from-[#133955] to-[#54BCE6] grid place-items-center">
                    <span className="font-display text-white/90 text-lg font-bold px-6 text-center">{p.title}</span>
                  </div>
                )}
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {(p.tags || []).map((t) => (
                      <span key={t} className="rounded-full bg-[#EAF7FC] text-[#133955] px-2.5 py-0.5 text-[11px] font-bold">{t}</span>
                    ))}
                  </div>
                  <h2 className="font-display text-lg font-bold text-[#133955] group-hover:text-[#54BCE6] transition-colors">{p.title}</h2>
                  {p.excerpt && <p className="text-sm text-[#173042]/70 mt-2 flex-1">{p.excerpt}</p>}
                  <p className="text-xs text-[#173042]/50 mt-3 flex items-center gap-1.5">
                    <CalendarDays size={12} /> {fmtDate(p.published_at)} · {p.author_name}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
